import React from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Container, 
  Grid,
  Card,
  CardContent,
  Paper
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../../app/contexts/AuthContext';
import productImage from '../../app/assets/product-screenshot.png'; // You'll need to add this image
import discordIcon from '../../app/assets/discord.svg';
import feature1 from '../../app/assets/f_1.png';
import feature2 from '../../app/assets/f_2.png';
import feature3 from '../../app/assets/f_3.png';
import feature4 from '../../app/assets/f_4.png';
import feature5 from '../../app/assets/f_5.png';

const FeatureCard = ({ title, description }) => (
  <Card 
    elevation={0} 
    sx={{ 
      height: '100%',
      backgroundColor: 'transparent',
      border: '1px solid',
      borderColor: 'divider'
    }}
  >
    <CardContent>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </CardContent>
  </Card>
);

const ResultCard = ({ title, description }) => (
  <Card 
    elevation={0} 
    sx={{ 
      height: '100%',
      backgroundColor: 'transparent',
      border: '1px solid',
      borderColor: 'divider'
    }}
  >
    <CardContent>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </CardContent>
  </Card>
);

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleAction = () => {
    if (user) {
      navigate('/app/meetings');
    } else {
      navigate('/signup');
    }
  };

  const features = [
    {
      title: "Team Setup",
      description: "Set up your team members and their profiles with ease.",
      image: feature1
    },
    {
      title: "1:1 Tracking",
      description: "Track and manage one-on-one meetings efficiently.",
      image: feature2
    },
    {
      title: "AI-Powered Planning",
      description: "Develop personalized career plans with our AI tools.",
      image: feature3
    },
    {
      title: "Progress Monitoring",
      description: "Capture blockers, action items, and goals.",
      image: feature4
    },
    {
      title: "Performance Management (Coming soon)",
      description: "Use our appraisal tools for data-driven feedback.",
      image: feature5
    }
  ];

  const results = [
    {
      title: "Improved Team Morale",
      description: "Better clarity on career progression and development opportunities"
    },
    {
      title: "Faster Problem Solving",
      description: "Fewer blockers going unnoticed with systematic tracking"
    },
    {
      title: "Streamlined Process",
      description: "Consistent performance management and feedback systems"
    }
  ];

  return (
    <Box>
      <Header />
      <Container maxWidth="lg">
        {/* Hero Section */}
        <Box
          sx={{
            py: 12,
            textAlign: 'center'
          }}
        >
          <Typography
            variant="h2"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 700,
              mb: 3,
              fontSize: { xs: '2.5rem', md: '3.5rem' }
            }}
          >
            Great leadership isn't just about managing—it's about growing people
          </Typography>
          <Typography
            variant="h5"
            sx={{
              mb: 6,
              color: 'text.secondary',
              maxWidth: '800px',
              mx: 'auto'
            }}
          >
            Tivra helps managers focus on their team's development while reducing the manual effort required to stay organized.
          </Typography>
          
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, fontSize: '2rem', paddingTop: '5rem' }}>
            Get free early access?
          </Typography>
          
          <Button
            variant="contained"
            size="large"
            onClick={handleAction}
            sx={{
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              textTransform: 'none',
              borderRadius: '28px',
              backgroundColor: '#3366FF',
              '&:hover': {
                backgroundColor: '#2952CC'
              }
            }}
          >
            {user ? 'Go to app' : 'Sign up here'}
          </Button>
          
          <Typography 
            variant="subtitle1" 
            color="primary" 
            sx={{ mt: 2, fontWeight: 500 }}
          >
            Hurry! Limited spots available
          </Typography>
        </Box>

        {/* Product Image */}
        <Box
          sx={{
            my: 8,
            textAlign: 'center'
          }}
        >
          <Box
            component="img"
            src={productImage}
            alt="Tivra Interface"
            sx={{
              maxWidth: '100%',
              height: 'auto',
              borderRadius: 5,
              boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
              transform: 'translateY(-20px)',
              transition: 'transform 0.3s ease-in-out',
              '&:hover': {
                transform: 'translateY(-25px)'
              }
            }}
          />
        </Box>

        {/* Discord Button Section */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          my: 8 
        }}>
          <Button
            variant="contained"
            startIcon={<Box component="img" src={discordIcon} sx={{ width: 20, height: 20 }} />}
            onClick={() => window.open('https://discord.gg/M725NayZ', '_blank')}
            sx={{
              backgroundColor: '#000000',
              color: '#ffffff',
              textTransform: 'none',
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              borderRadius: '28px',
              '&:hover': {
                backgroundColor: '#333333'
              }
            }}
          >
            Join us on Discord
          </Button>
        </Box>

        {/* Features Section */}
        <Box sx={{ py: 12 }}>
          <Typography
            variant="h3"
            component="h2"
            textAlign="center"
            gutterBottom
            sx={{ mb: 8, fontWeight: 700 }}
          >
            Features
          </Typography>

          <Box sx={{display:'flex', justifyContent:'center', alignItems:'center'}}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: '800px'}}>
            {features.map((feature, index) => (
              <Paper
                key={index}
                elevation={0}
                sx={{
                  p: 4,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  borderRadius: 4,
                  backgroundColor: '#ffffff',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.08)'
                  }
                }}
              >
                {/* Image container */}
                <Box
                  sx={{
                    width: '280px',
                    height: '200px',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Box
                    component="img"
                    src={feature.image}
                    alt={feature.title}
                    sx={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain'
                    }}
                  />
                </Box>

                {/* Content */}
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="h4"
                    sx={{
                      mb: 2,
                      fontWeight: 700,
                      fontSize: { xs: '1.5rem', md: '2rem' }
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{
                      fontSize: { xs: '1rem', md: '1.1rem' },
                      lineHeight: 1.6
                    }}
                  >
                    {feature.description}
                  </Typography>
                </Box>
              </Paper>
            ))}
          </Box>
          </Box>
        </Box>

        {/* Results Section */}
        <Box sx={{ py: 8 }}>
          <Typography
            variant="h3"
            component="h2"
            textAlign="center"
            gutterBottom
            sx={{ mb: 6, fontWeight: 700 }}
          >
            Results You Can Expect
          </Typography>
          <Grid container spacing={4}>
            {results.map((result, index) => (
              <Grid item xs={12} md={4} key={index}>
                <ResultCard {...result} />
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Call to Action Section */}
        <Box
          sx={{
            py: 12,
            textAlign: 'center'
          }}
        >
          <Typography
            variant="h3"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 700 }}
          >
            Try Tivra For Free Today
          </Typography>
          <Typography
            variant="h6"
            sx={{
              mb: 4,
              color: 'text.secondary',
              maxWidth: '600px',
              mx: 'auto'
            }}
          >
            We'd love your feedback! Try Tivra and let us know how it fits your needs.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={handleAction}
            sx={{
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              textTransform: 'none',
              borderRadius: '28px',
              backgroundColor: '#3366FF',
              '&:hover': {
                backgroundColor: '#2952CC'
              }
            }}
          >
            {user ? 'Go to app' : 'Sign up here'}
          </Button>
        </Box>

        <Footer />
      </Container>
    </Box>
  );
} 